import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, filter, mergeMap, of, tap } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import * as AuthActions from './auth.actions';
import { SocialAuthService } from "@abacritt/angularx-social-login";
import { Store } from "@ngrx/store";
import { showErrorToast } from "../../../shared/store/toast/toast.actions";
import { refreshTokenSuccess } from "./auth.actions";
import * as Sentry from "@sentry/angular";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { ErrorCodeService } from "../../../shared/services/error-code.service";

@Injectable()
export class AuthEffects {

  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private socialAuthService: SocialAuthService,
    private readonly store: Store,
    private errorCodeService: ErrorCodeService
  ) {}

  /** The message the login form shows, reported to Sentry under the reference it carries. */
  private loginFailure(error: any, surface: string) {
    return AuthActions.loginFailed({
      error,
      message: this.errorCodeService.formatErrorForDisplay(error, { tags: { surface } }),
    });
  }

  loginWithGoogle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginWithGoogle),
      switchMap(({ idToken }) =>
        this.authService.loginWithGoogle(idToken).pipe(
          map(credentials => {
            return credentials;
          }),

          map(credentials => AuthActions.loginSuccess({ credentials })),
          catchError(error => of(this.loginFailure(error, 'login-google')))
        )
      )
    )
  );

  loginWithApple$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginWithApple),
      switchMap(({ idToken, firstName }) =>
        this.authService.loginWithApple(idToken, firstName).pipe(
          map(credentials => {
            return credentials;
          }),
          map(credentials => AuthActions.loginSuccess({ credentials })),
          catchError(error => of(this.loginFailure(error, 'login-apple')))
        )
      )
    )
  );

  checkGoogleAuthState$ = createEffect(() =>
    this.socialAuthService.authState.pipe(
      mergeMap((user) => {
        if (user) {
          // Assuming the user object contains an idToken or some identifier you can use
          return of(AuthActions.loginWithGoogle({ idToken: user.idToken }));
        } else {
          // No user found, you might want to handle this case differently
          return EMPTY; // Make sure to import EMPTY from 'rxjs'
        }
      })
    )
  );

  sendEmailVerificationCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.sendEmailVerificationCode),
      switchMap(({ email }) => this.authService.sendEmailVerificationCode(email).pipe(
        map(data => AuthActions.sendEmailVerificationCodeSuccess({ email, userId: data.authGetEmailVerificationCode.userId })),
        catchError(error => of(AuthActions.sendEmailVerificationCodeFailure({ error })))
      )
    )
  ));

  verifyEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginWithEmailOtp),
      switchMap(({ userId, otp }) => this.authService.verifyEmail(userId, otp).pipe(
        map(credentials => AuthActions.loginSuccess({ credentials })),
        catchError(error => of(this.loginFailure(error, 'login-email-otp')))
      )
    ))
  );

  storeCredentials$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess, refreshTokenSuccess),
      tap(({ credentials }) => {
        this.authService.storeCredentials(credentials);
      })
    ),
    { dispatch: false }
  );

  clearCredentials$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      tap(() => {
        this.authService.clearCredentials();
      })
    ),
    { dispatch: false });

  identifyUserInSentry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      tap(({ credentials }) => {
        try {
          const { sub } = jwtDecode<JwtPayload>(credentials.accessToken);
          if (sub) {
            Sentry.setUser({ id: sub });
          }
        } catch (error) {
          console.error('Error decoding JWT token', error);
        }
      })
    ),
    { dispatch: false }
  );

  clearUserInSentry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      tap(() => {
        Sentry.setUser(null);
      })
    ),
    { dispatch: false }
  );

  refreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshToken),
      mergeMap(() =>
        this.authService.refreshToken().pipe(
          map(tokenPair => AuthActions.refreshTokenSuccess({
            credentials: tokenPair
          })),
          catchError(error => {
            return of(AuthActions.refreshTokenFailure({ error }));
          })
        )
      )
    )
  );

  logoutOnUnauthenticatedRefresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshTokenFailure),
      filter(({ error }) => error?.extensions?.code === 'UNAUTHENTICATED'),
      map(() => AuthActions.logout())
    )
  );

}
